import { useCallback, useEffect, useState } from "react";
import { Database, MessageCircle, RefreshCw, Server, Zap } from "lucide-react";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Page, PageBody, PageHeader, Skeleton, type Tone } from "@/ui";
import { api, ApiClientError } from "@/lib/api";

/**
 * The platform's own status page.
 *
 * It lives on its own screen rather than as a strip on the Organizations list
 * because D-08 accepted WhatsApp as a single point of failure for every
 * karigar login in every organization — that needs somewhere it can be looked
 * at deliberately, not a line squeezed under a table.
 */

interface HealthResponse {
  ok: boolean;
  services: {
    mongodb?: string;
    cache?: string;
    whatsapp?: string;
  };
  at: string;
}

const POLL_MS = 15_000;

interface ServiceView {
  key: keyof HealthResponse["services"];
  label: string;
  icon: typeof Database;
  detail: (value: string | undefined) => string;
}

const SERVICES: ServiceView[] = [
  {
    key: "mongodb",
    label: "MongoDB",
    icon: Database,
    detail: (v) =>
      v === "up"
        ? "Connected. All reads and writes are available."
        : "Unreachable. The application cannot serve any request.",
  },
  {
    key: "cache",
    label: "Cache",
    icon: Zap,
    detail: (v) =>
      v === "redis"
        ? "Redis connected. Sessions, rate limits and OTP codes are shared across instances."
        : v === "in-process"
          ? "Running on the in-process fallback. Fine for development; production requires Redis, because sign-out, rate limits and queues all depend on it."
          : "Unreachable.",
  },
  {
    key: "whatsapp",
    label: "WhatsApp delivery",
    icon: MessageCircle,
    detail: (v) =>
      v === "aisensy_configured"
        ? "AiSensy API key and at least one campaign are configured. Delivery must be checked in AiSensy."
        : v === "aisensy_key_saved"
        ? "AiSensy API key is saved. Enable a campaign to route messages through it."
        : v === "up"
        ? "Connected. OTP codes and notifications are being delivered."
        : v === "awaiting_scan"
          ? "Running, but no phone is linked. Scan its QR code to start delivering codes."
          : v === "dev_mode"
          ? "Development OTP mode is on, so codes are written to the server log instead of sent. Refused at startup in production."
          : v === "not_configured"
            ? "Not connected yet. No karigar can receive a sign-in code."
            : "Down. No karigar in any organization can receive a sign-in code.",
  },
];

function toneFor(value: string | undefined): Tone {
  if (value === "up" || value === "redis") return "success";
  if (value === "in-process" || value === "not_configured" || value === "dev_mode" || value === "awaiting_scan" || value === "aisensy_configured" || value === "aisensy_key_saved")
    return "warning";
  if (value === undefined) return "neutral";
  return "danger";
}

function labelFor(value: string | undefined): string {
  if (value === undefined) return "Unknown";
  if (value === "up") return "Operational";
  if (value === "aisensy_configured") return "AiSensy configured";
  if (value === "aisensy_key_saved") return "AiSensy key saved";
  if (value === "redis") return "Redis";
  if (value === "in-process") return "Fallback";
  if (value === "not_configured") return "Not connected";
  if (value === "awaiting_scan") return "Waiting for scan";
  if (value === "dev_mode") return "Development mode";
  return "Down";
}

export function SystemHealthPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      // A 503 body still carries the per-service detail, which is exactly what
      // this page exists to show — so read it rather than treating it as a
      // blank failure.
      const data = await api.get<HealthResponse>("/health/ready");
      setHealth(data);
      setError(null);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 503 && err.details.services) {
        // The API answered; a dependency is down. Show which one.
        setHealth(err.details as unknown as HealthResponse);
        setError(null);
      } else if (err instanceof ApiClientError) {
        setError(err.message);
      } else if (err instanceof SyntaxError) {
        setError("The health endpoint returned something unreadable.");
      } else {
        setError("Could not reach the API.");
      }
    } finally {
      setLoading(false);
      setCheckedAt(new Date());
    }
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const services = health?.services ?? {};
  const degraded = SERVICES.some((s) => {
    const tone = toneFor(services[s.key]);
    return tone === "danger" || tone === "warning";
  });

  return (
    <Page>
      <PageHeader
        action={
          <Button
            icon={<RefreshCw aria-hidden="true" className="size-4" />}
            loading={loading}
            onClick={() => void load()}
            size="sm"
            variant="secondary"
          >
            Check now
          </Button>
        }
        title="System health"
      />

      <PageBody>
        {error ? (
          <div className="mb-5">
            <Alert kind="danger" title="The API is not responding">
              {error}
            </Alert>
          </div>
        ) : !loading && degraded ? (
          <div className="mb-5">
            <Alert kind="warning" title="Something needs attention">
              One or more services are not running normally. See the detail below.
            </Alert>
          </div>
        ) : null}

        <div className="grid gap-3 lg:grid-cols-3">
          {SERVICES.map((service) => {
            const value = services[service.key];
            const Icon = service.icon;
            return (
              <Card key={service.key}>
                <CardBody className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2.5 font-title text-base font-semibold text-ink">
                      <Icon aria-hidden="true" className="size-[1.125rem] text-muted" strokeWidth={1.75} />
                      {service.label}
                    </span>
                    {loading && !health ? (
                      <Skeleton className="h-5 w-20" />
                    ) : (
                      <Badge tone={toneFor(value)}>{labelFor(value)}</Badge>
                    )}
                  </div>
                  {/* A <div>, not a <p>: Skeleton renders a <div>, and a block
                      element inside a <p> is invalid HTML the browser silently
                      un-nests, which broke hydration. */}
                  <div className="text-sm text-muted">
                    {loading && !health ? <Skeleton className="h-4 w-full" /> : service.detail(value)}
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>

        <div className="mt-5">
          <Card>
            <CardHeader title="Details" />
            <CardBody className="flex flex-col gap-3 text-sm">
              <Row label="API">
                <span className="flex items-center gap-2">
                  <Server aria-hidden="true" className="size-4 text-muted" strokeWidth={1.75} />
                  {error ? "Not responding" : "Responding"}
                </span>
              </Row>
              <Row label="Last checked">
                {checkedAt ? checkedAt.toLocaleTimeString() : "—"}
              </Row>
              <Row label="Server time">
                {health?.at ? new Date(health.at).toLocaleString() : "—"}
              </Row>
            </CardBody>
          </Card>
        </div>
      </PageBody>
    </Page>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-rule pb-2.5 last:border-0 last:pb-0">
      <span className="text-muted">{label}</span>
      <span className="text-ink">{children}</span>
    </div>
  );
}
